import * as MuseumActions from '@Actions/museum.actions';
import * as slmDetailsActions from '@Actions/slmDetails.actions';
import { AuthService } from '@Services/auth.service';
import { MuseumService } from '@Services/museum.service';
import { RewardsService } from '@Services/rewards.service';
import { TicketPurchaseService } from '@Services/ticket-purchase.service';
import { AppState } from '@State/app.state';
import { mapMuseumToMuseumDetails } from '@Utils/ticket-utils';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, concat, filter, forkJoin, map, mergeMap, of, withLatestFrom } from 'rxjs';

@Injectable()
export class MuseumEffects {

  private readonly rewardsSvc = inject(RewardsService);
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store<AppState>);
  private readonly museumSvc = inject(MuseumService);
  private readonly ticketSvc = inject(TicketPurchaseService);
  private readonly authSvc = inject(AuthService);

  loadSingleMuseum$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MuseumActions.loadSingleMuseum),
      withLatestFrom(this.store.select((state) => state.selectedMuseum.museum)),
      filter(
        ([action, museum]) =>
          !museum ||
          (action.codesr !== undefined && museum.id !== action.codesr)
      ),
      mergeMap(([action, museum]) => {
        const { codesr } = action;

        if (codesr && codesr.length > 0) {
          return this.museumSvc.getSingleMuseum(codesr).pipe(
            mergeMap((singleMuseum) =>
              concat(
                of(MuseumActions.setSingleMuseum({ museum: singleMuseum })),
                of(slmDetailsActions.setSingleMuseumDetails({ museumDetails: mapMuseumToMuseumDetails(singleMuseum) })),
                of(MuseumActions.doneLoadingSingleMuseumInfos())
              )
            ),
            catchError((error) =>
              concat(
                of(
                  MuseumActions.singleMuseumLoadError({
                    museumLoadError: error,
                  })
                ),
                of(MuseumActions.doneLoadingSingleMuseumInfos())
              )
            )
          );
        } else {
          return concat(
            of(
              MuseumActions.singleMuseumLoadError({
                museumLoadError: new Error('No valid parameter provided.'),
              })
            ),
            of(MuseumActions.doneLoadingSingleMuseumInfos())
          );
        }
      })
    )
  );

  clearMuseumLoadErrorOnNextLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MuseumActions.loadSingleMuseum),
      withLatestFrom(this.store.select((state) => state.selectedMuseum)),
      filter(([action, museumState]) => !!museumState.museumLoadError),
      map(() => MuseumActions.clearSingleMuseumLoadError())
    )
  );

  loadSingleMuseumVisitList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MuseumActions.loadSingleMuseumVisitList),
      withLatestFrom(
        this.store.select((state) => state.selectedMuseum.visitsList),
        this.store.select((state) => state.selectedMuseum.lang)
      ),
      filter(([action, visitList, cachedLang]) => {
        return visitList.length < 1 || !cachedLang ||  action.lang.lang_code !== cachedLang.lang_code
      }),
      mergeMap(([action, visitList]) => {
        const codesr = action.codesr;
        if (codesr && codesr.length > 0) {

          const museumOffers$ = this.museumSvc.getMuseumOffers(codesr);

          const rewardsPoints$ = this.rewardsSvc.fetchVisitsPoints(codesr).pipe(
            catchError(_err => {
              return of(new Map<number, number>());
            })
          );

          const userPointsStatus$ = this.authSvc.isAuthenticated()
            ? this.rewardsSvc.fetchUserVisitsPointsStatus(codesr).pipe(
                catchError(_err => {
                  return of([] as number[]);
                })
              )
            : of([] as number[]);

          const userVisitsWithAvailableVouchers$ = this.authSvc.isAuthenticated()
            ? this.rewardsSvc.fetchUserVoucherAvailabiltyForVisits(codesr).pipe(
                catchError(_err => {
                  return of([] as number[]);
                })
              )
            : of([] as number[]);

          return forkJoin([museumOffers$, rewardsPoints$, userPointsStatus$, userVisitsWithAvailableVouchers$]).pipe(
            map(([offerResponse, pointsMap, userPointsStatus, userVisitsWithAvailableVouchers]) => {
              this.ticketSvc.enableCaptcha.next(offerResponse.captchaEnabled);

              const enrichedValues = offerResponse.values.map(visit => {
                if (!visit) return visit;

                const points = pointsMap.get(visit.idezon);

                const pointsRedeemed = !!userPointsStatus.find(v => v === visit.idezon);
                const voucherAvailable = !!userVisitsWithAvailableVouchers.find(v => v === visit.idezon)

                return {
                  ...visit,
                  points: points !== undefined ? points : null,
                  pointsRedeemed: pointsRedeemed,
                  voucherAvailable: voucherAvailable
                };
              });

              return {
                ...offerResponse,
                values: enrichedValues
              };
            }),
            mergeMap((enrichedVisitList) =>
              concat(
                of(
                  MuseumActions.setSingleMuseumVisitList({
                    visitsList: enrichedVisitList.values,
                  }),
                  MuseumActions.setLanguage({ lang: action.lang }),
                ),
                of(MuseumActions.doneLoadingSingleMuseumVisits())
              )
            ),
            catchError((error) =>
              concat(
                of(
                  MuseumActions.singleMuseumVisitListLoadError({
                    visitLoadError: error,
                  })
                ),
                of(MuseumActions.doneLoadingSingleMuseumVisits())
              )
            )
          );

        } else {
          return concat(
            of(
              MuseumActions.singleMuseumVisitListLoadError({
                visitLoadError: new Error('No valid parameter provided.'),
              })
            ),
            of(MuseumActions.doneLoadingSingleMuseumVisits())
          );
        }
      })
    )
  );

  clearMuseumVisitsLoadErrorOnNextLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MuseumActions.loadSingleMuseumVisitList),
      withLatestFrom(this.store.select((state) => state.selectedMuseum)),
      filter(([action, museumState]) => !!museumState.visitLoadError),
      map(() => MuseumActions.clearSingleMuseumVisitListLoadError())
    )
  );

  loadMuseumByPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MuseumActions.loadMuseumsByPage),
      withLatestFrom(
        this.store.select((state) => state.allMuseumsPaginated.paginationData)
      ),
      mergeMap(([action, paginationData]) => {
        const { page, pageSize } = action;
        if (paginationData[page]) {
          // se la pagina e' gia stata caricata, skippa
          return of(MuseumActions.museumsPaginatedAlreadyLoaded());
        } else {
          // altrimenti carica la pagina e salva in cache
          return this.museumSvc
            .getAllPublishedMuseums(undefined, page, pageSize)
            .pipe(
              map((response) =>
                MuseumActions.setMuseumsForPage({
                  page: page,
                  museums: response.results,
                })
              ),
              catchError((error) =>
                of(MuseumActions.museumsPaginatedLoadError({ error: error }))
              )
            );
        }
      })
    )
  );

  loadVisitorInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MuseumActions.loadVisitorInfo),
      withLatestFrom(
        this.store.select((state) => state.selectedMuseum.visitorInfo),
      ),
      filter(([ action, visitorInfo]) => {
        return !visitorInfo.info || visitorInfo.forMuseum !== action.codesr || visitorInfo.infoLang?.lang_code !== action.lang?.lang_code
      }),
      mergeMap(([action]) => {
        const codesr = action.codesr;
        const lang = action.lang;

        return this.museumSvc.getMuseumVisitorsInfo(codesr)
        .pipe(
            map((visitorInfo) => {
              return MuseumActions.setVisitorInfo({ info: visitorInfo, infoLang: lang, forMuseum: codesr })
            }),
            catchError((_error) => {
              return of(MuseumActions.setVisitorInfo({ info: null, infoLang: null, forMuseum: null }))
            })
          )
      })
    )
  )
}
